// src/components/DailyVehicleCheck.js
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  submitVehicleCheck,
  getTodayCheckForDriverAndVehicle,
  updateVehicleCheck,
} from "../services/vehicleCheckService";
import { sendServiceAlertEmail } from "../services/emailService";
import { useNavigate } from "react-router-dom";

// ExternalConditionItem component - defined outside to prevent re-creation on each render
const ExternalConditionItem = ({ label, conditionName, commentName, formData, setFormData, handleInputChange }) => (
  <div className="mb-6 pb-6 border-b border-gray-200 last:border-b-0 last:pb-0 last:mb-0">
    <p className="text-sm font-semibold text-gray-800 mb-3">{label}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Condition
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: "satisfactory", label: "√", color: "green" },
            { value: "defective", label: "X", color: "red" },
            { value: "not-applicable", label: "N/A", color: "gray" },
          ].map((status) => (
            <button
              key={status.value}
              type="button"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  [conditionName]: status.value,
                }))
              }
              className={`py-2 text-sm rounded-lg border-2 transition-all font-bold ${
                formData[conditionName] === status.value
                  ? status.color === "green"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : status.color === "red"
                    ? "bg-red-100 border-red-500 text-red-700"
                    : "bg-gray-100 border-gray-500 text-gray-700"
                  : "bg-white border-gray-300 text-gray-600"
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Comment
        </label>
        <textarea
          name={commentName}
          value={formData[commentName]}
          onChange={handleInputChange}
          rows="2"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="Add comment..."
        />
      </div>
    </div>
  </div>
);

const initialFormData = {
  // Vehicle Information
  vehicleRegistrationNo: "",
  vehicleMake: "",
  odometerReading: "",
  vehicleOperator: "",
  lastServiceMileage: "",
  lastServiceDate: "",
  nextServiceMileage: "",
  controlTechniqueDate: "",
  insuranceExpirationDate: "",
  vehicleNotes: "",

  fuelLevel: "",

  // External Vehicle Condition
  externalBodyworkCondition: "satisfactory",
  externalBodyworkComment: "",
  externalWiperBladesCondition: "satisfactory",
  externalWiperBladesComment: "",
  externalCleannessCondition: "satisfactory",
  externalCleannessComment: "",
  externalTyresCondition: "satisfactory",
  externalTyresComment: "",

  // Vehicle Interior and Equipment
  interiorSeatBeltsCondition: "satisfactory",
  interiorSeatBeltsComment: "",
  interiorFirstAidKitCondition: "satisfactory",
  interiorFirstAidKitComment: "",
  interiorFireExtinguisherCondition: "satisfactory",
  interiorFireExtinguisherComment: "",
  interiorTorchCondition: "satisfactory",
  interiorTorchComment: "",
  interiorWarningTriangleCondition: "satisfactory",
  interiorWarningTriangleComment: "",
  interiorLogBookCondition: "satisfactory",
  interiorLogBookComment: "",
  interiorSpareTyreCondition: "satisfactory",
  interiorSpareTyreComment: "",

  // Fluids
  fluidsEngineOilCondition: "satisfactory",
  fluidsEngineOilComment: "",
  fluidsCoolantCondition: "satisfactory",
  fluidsCoolantComment: "",
  fluidsWindscreenWashCondition: "satisfactory",
  fluidsWindscreenWashComment: "",
  fluidsBrakeClutchCondition: "satisfactory",
  fluidsBrakeClutchComment: "",
  fluidsPowerSteeringCondition: "satisfactory",
  fluidsPowerSteeringComment: "",
  fluidsBatteryCondition: "satisfactory",
  fluidsBatteryComment: "",
  fluidsLeaksCondition: "satisfactory",
  fluidsLeaksComment: "",

  // Function Checks Before Starting Journey
  functionWarningLightCondition: "satisfactory",
  functionWarningLightComment: "",
  functionAllLightsCondition: "satisfactory",
  functionAllLightsComment: "",
  functionHornCondition: "satisfactory",
  functionHornComment: "",
  functionWashersWipersCondition: "satisfactory",
  functionWashersWipersComment: "",
  functionBrakeCondition: "satisfactory",
  functionBrakeComment: "",
  functionFuelCondition: "satisfactory",
  functionFuelComment: "",

  // Driver Declaration
  driverSignature: false,
};

const DailyVehicleCheck = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [driverData, setDriverData] = useState(null);
  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleTodayChecks, setVehicleTodayChecks] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState(initialFormData);

  // Today's check for the currently selected vehicle
  const todayCheck = selectedVehicle ? (vehicleTodayChecks[selectedVehicle.id] ?? null) : null;

  const populateFormForVehicle = (vehicle, driver, check) => {
    const base = {
      ...initialFormData,
      vehicleRegistrationNo: vehicle.registration_no || "",
      vehicleMake: `${vehicle.make || ""} ${vehicle.model || ""}`.trim(),
      odometerReading: vehicle.mileage?.toString() || "",
      vehicleOperator: driver.name || "",
      controlTechniqueDate: vehicle.controlTechniqueDate || "",
      insuranceExpirationDate: vehicle.insuranceExpirationDate || "",
      lastServiceMileage: vehicle.lastServiceMileage?.toString() || "",
      lastServiceDate: vehicle.lastServiceDate || "",
      nextServiceMileage: vehicle.nextServiceMileage?.toString() || "",
    };

    if (check?.status === "rejected") {
      Object.keys(initialFormData).forEach((key) => {
        if (check[key] !== undefined) base[key] = check[key];
      });
      base.driverSignature = false;
    }

    setFormData(base);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        // Fetch driver information
        const driversRef = collection(db, "drivers");
        const driverQuery = query(driversRef, where("uid", "==", currentUser.uid));
        const driverSnapshot = await getDocs(driverQuery);

        if (!driverSnapshot.empty) {
          const driver = {
            id: driverSnapshot.docs[0].id,
            ...driverSnapshot.docs[0].data(),
          };
          setDriverData(driver);

          // Fetch ALL active vehicle assignments
          const assignmentsRef = collection(db, "vehicle_assignments");
          const assignmentQuery = query(
            assignmentsRef,
            where("driver_id", "==", driver.id),
            where("status", "==", "active")
          );
          const assignmentSnapshot = await getDocs(assignmentQuery);

          const vehicles = [];
          const todayChecks = {};

          for (const assignDoc of assignmentSnapshot.docs) {
            const assignment = assignDoc.data();
            const vehicleRef = doc(db, "vehicles", assignment.vehicle_id);
            const vehicleDoc = await getDoc(vehicleRef);

            if (vehicleDoc.exists()) {
              const vehicle = {
                id: vehicleDoc.id,
                ...vehicleDoc.data(),
                assignmentId: assignDoc.id,
                startDate: assignment.start_date,
                endDate: assignment.end_date,
              };
              vehicles.push(vehicle);

              const check = await getTodayCheckForDriverAndVehicle(driver.id, vehicle.id);
              todayChecks[vehicle.id] = check;
            }
          }

          setAssignedVehicles(vehicles);
          setVehicleTodayChecks(todayChecks);

          // Auto-select when only one vehicle is assigned
          if (vehicles.length === 1) {
            setSelectedVehicle(vehicles[0]);
            populateFormForVehicle(vehicles[0], driver, todayChecks[vehicles[0].id] ?? null);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowSuccess(false);
    populateFormForVehicle(vehicle, driverData, vehicleTodayChecks[vehicle.id] ?? null);
  };

  const handleBackToVehicleList = () => {
    setSelectedVehicle(null);
    setShowSuccess(false);
    setFormData(initialFormData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedVehicle) {
      alert("No vehicle selected. Please select a vehicle.");
      return;
    }

    if (!formData.odometerReading || !formData.fuelLevel) {
      alert("Please fill in odometer reading and fuel level.");
      return;
    }

    if (!formData.driverSignature) {
      alert("Please confirm that all reported information is correct.");
      return;
    }

    setSubmitting(true);

    try {
      const checkData = {
        driverId: driverData.id || "",
        driverName: driverData.name || "",
        driverPhone: driverData.phone || "",
        vehicleId: selectedVehicle.id || "",
        vehiclePlate: selectedVehicle.registration_no || "",
        vehicleInfo: `${selectedVehicle.make || ""} ${selectedVehicle.model || ""} ${selectedVehicle.year || ""}`.trim(),
        ...formData,
        checkDate: new Date().toISOString().split("T")[0],
      };

      if (todayCheck?.status === "rejected") {
        await updateVehicleCheck(todayCheck.id, checkData);
      } else {
        await submitVehicleCheck(checkData);
      }

      // Service alert — email admin if <= 500 km to next service
      const odometer = Number(formData.odometerReading);
      const nextService = Number(formData.nextServiceMileage);
      if (formData.nextServiceMileage && odometer) {
        const remaining = nextService - odometer;
        if (remaining <= 500) {
          sendServiceAlertEmail({
            driverName: driverData.name,
            vehiclePlate: selectedVehicle.registration_no,
            vehicleInfo: `${selectedVehicle.make || ''} ${selectedVehicle.model || ''}`.trim(),
            odometerReading: odometer,
            nextServiceMileage: nextService,
            remaining,
          });
        }
      }

      // Update local today-check state so the vehicle list reflects the new status
      setVehicleTodayChecks((prev) => ({
        ...prev,
        [selectedVehicle.id]: { ...checkData, status: 'pending' },
      }));

      setShowSuccess(true);
      setTimeout(() => {
        if (assignedVehicles.length > 1) {
          setShowSuccess(false);
          setSelectedVehicle(null);
          setFormData(initialFormData);
        } else {
          navigate("/driver-dashboard");
        }
      }, 2000);
    } catch (error) {
      console.error("Error submitting check:", error);
      alert("Failed to submit vehicle check. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (assignedVehicles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <svg
              className="w-16 h-16 text-yellow-600 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              No Vehicle Assigned
            </h2>
            <p className="text-gray-600 mb-4">
              You don't have a vehicle assigned yet. Please contact your
              administrator.
            </p>
            <button
              onClick={() => navigate("/driver-dashboard")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Vehicle selection screen — shown when driver has multiple vehicles and none is selected yet
  if (assignedVehicles.length > 1 && !selectedVehicle) {
    const allDone = assignedVehicles.every(
      (v) => vehicleTodayChecks[v.id] && vehicleTodayChecks[v.id].status !== "rejected"
    );

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-blue-600 text-white p-4 sticky top-0 z-10 shadow-md">
          <div className="max-w-2xl mx-auto flex items-center">
            <button onClick={() => navigate("/driver-dashboard")} className="mr-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold">Daily Vehicle Check</h1>
              <p className="text-sm text-blue-100">
                {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4">
          <p className="text-sm text-gray-500 mb-4">
            You are assigned to {assignedVehicles.length} vehicles. Select one to start or view today's check.
          </p>

          {allDone && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-green-800">All vehicles checked for today.</p>
            </div>
          )}

          <div className="space-y-3">
            {assignedVehicles.map((vehicle) => {
              const check = vehicleTodayChecks[vehicle.id];
              const isDone = check && check.status !== "rejected";
              const isRejected = check?.status === "rejected";

              return (
                <div
                  key={vehicle.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="font-semibold text-gray-900">{vehicle.registration_no}</p>
                    <p className="text-sm text-gray-500">{`${vehicle.make || ""} ${vehicle.model || ""}`.trim()}</p>
                    {check && (
                      <span
                        className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                          check.status === "approved"
                            ? "bg-green-100 text-green-700"
                            : check.status === "rejected"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {check.status.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleVehicleSelect(vehicle)}
                    className={`ml-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isDone
                        ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        : isRejected
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    {isDone ? "View" : isRejected ? "Fix & Resubmit" : "Start Check"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (todayCheck && todayCheck.status !== "rejected") {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <svg
              className="w-16 h-16 text-green-600 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Check Already Submitted
            </h2>
            <p className="text-gray-600 mb-2">
              You have already submitted today's check for{" "}
              <span className="font-semibold">{selectedVehicle?.registration_no}</span>.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Status:{" "}
              <span
                className={`font-semibold ${
                  todayCheck.status === "approved"
                    ? "text-green-600"
                    : todayCheck.status === "rejected"
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                {todayCheck.status.toUpperCase()}
              </span>
            </p>
            {assignedVehicles.length > 1 ? (
              <button
                onClick={handleBackToVehicleList}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Check Another Vehicle
              </button>
            ) : (
              <button
                onClick={() => navigate("/driver-dashboard")}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Back to Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {todayCheck?.status === "rejected" ? "Check Resubmitted!" : "Check Submitted!"}
          </h2>
          <p className="text-gray-600">
            {todayCheck?.status === "rejected"
              ? "Your corrected vehicle check has been resubmitted successfully."
              : "Your daily vehicle check has been submitted successfully."}
          </p>
          {assignedVehicles.length > 1 && (
            <p className="text-sm text-gray-400 mt-2">Returning to vehicle list…</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 sticky top-0 z-10 shadow-md">
        <div className="max-w-2xl mx-auto flex items-center">
          <button
            onClick={assignedVehicles.length > 1 ? handleBackToVehicleList : () => navigate("/driver-dashboard")}
            className="mr-3"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold">
              Daily Vehicle Check
              {selectedVehicle && (
                <span className="ml-2 text-base font-normal text-blue-200">
                  — {selectedVehicle.registration_no}
                </span>
              )}
            </h1>
            <p className="text-sm text-blue-100">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-4 space-y-6">
        {todayCheck?.status === "rejected" && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h2 className="text-lg font-bold text-red-800 mb-1">
              This report was rejected
            </h2>
            <p className="text-sm text-red-700">
              Raport mwatanze ntabwo yemejwe. Jya muri system uyikosore wongere uyitange.
            </p>
            {todayCheck.reviewNote && (
              <div className="mt-3 bg-white border border-red-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-red-700 mb-1">
                  Admin Review Note
                </p>
                <p className="text-sm text-gray-800">{todayCheck.reviewNote}</p>
              </div>
            )}
          </div>
        )}

        {/* Vehicle Information Section */}
        <div className="bg-blue-50 rounded-lg shadow-sm p-4 border-2 border-blue-200">
          <h2 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
            <svg
              className="w-6 h-6 mr-2 text-blue-600"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
            </svg>
            Vehicle Information
          </h2>

          <div className="bg-white rounded-lg p-4 mb-3">
            <p className="text-xs text-gray-600 mb-3">
              <strong>Legend:</strong> √ = satisfactory/available | X =
              defective/missing | N/A = Not applicable
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200">
              <div>
                <p className="text-xs text-gray-500">
                  Vehicle Registration No:
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {formData.vehicleRegistrationNo}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Vehicle Make:</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formData.vehicleMake || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Odometer Reading (km) *
                </label>
                <input
                  type="number"
                  name="odometerReading"
                  value={formData.odometerReading}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Current odometer"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Service Mileage
                </label>
                <input
                  type="number"
                  name="lastServiceMileage"
                  value={formData.lastServiceMileage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Last service km"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Service Date
                </label>
                <input
                  type="date"
                  name="lastServiceDate"
                  value={formData.lastServiceDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Next Service Mileage
                </label>
                <input
                  type="number"
                  name="nextServiceMileage"
                  value={formData.nextServiceMileage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Next service km"
                />
                {(() => {
                  const remaining = Number(formData.nextServiceMileage) - Number(formData.odometerReading);
                  if (!formData.nextServiceMileage || !formData.odometerReading) return null;
                  if (remaining <= 0) return (
                    <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-300 text-red-700 text-sm px-3 py-2 rounded-lg">
                      <span>🚨</span>
                      <span>Service <strong>overdue</strong> by {Math.abs(remaining)} km!</span>
                    </div>
                  );
                  if (remaining <= 500) return (
                    <div className="mt-2 flex items-center gap-2 bg-orange-50 border border-orange-300 text-orange-700 text-sm px-3 py-2 rounded-lg">
                      <span>⚠️</span>
                      <span>Only <strong>{remaining} km</strong> until next service.</span>
                    </div>
                  );
                  return null;
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fuel Level (%) *
                </label>
                <input
                  type="number"
                  name="fuelLevel"
                  value={formData.fuelLevel}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Fuel percentage"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Control Technique Date
                </label>
                <input
                  type="date"
                  name="controlTechniqueDate"
                  value={formData.controlTechniqueDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Expiration Date
                </label>
                <input
                  type="date"
                  name="insuranceExpirationDate"
                  value={formData.insuranceExpirationDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                NOTE
              </label>
              <textarea
                name="vehicleNotes"
                value={formData.vehicleNotes}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Any additional vehicle information..."
              />
            </div>
          </div>
        </div>

        {/* External Vehicle Condition */}
        <div className="bg-white rounded-lg shadow-sm p-5 border-2 border-gray-300">
          <h3 className="font-bold text-lg text-gray-900 mb-4 uppercase">
            External Vehicle Condition
          </h3>
          <ExternalConditionItem
            label="Vehicle bodywork, windscreen, windows, lights"
            conditionName="externalBodyworkCondition"
            commentName="externalBodyworkComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Windscreen wiper blades"
            conditionName="externalWiperBladesCondition"
            commentName="externalWiperBladesComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Cleanness of windscreen, windows, mirrors, lights, number plate"
            conditionName="externalCleannessCondition"
            commentName="externalCleannessComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Tyres, tyre pressure, tyre wear"
            conditionName="externalTyresCondition"
            commentName="externalTyresComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
        </div>

        {/* Vehicle Interior and Equipment */}
        <div className="bg-white rounded-lg shadow-sm p-5 border-2 border-gray-300">
          <h3 className="font-bold text-lg text-gray-900 mb-4 uppercase">
            Vehicle Interior and Equipment
          </h3>
          <ExternalConditionItem
            label="Seat belts"
            conditionName="interiorSeatBeltsCondition"
            commentName="interiorSeatBeltsComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="First aid kit"
            conditionName="interiorFirstAidKitCondition"
            commentName="interiorFirstAidKitComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Fire extinguisher"
            conditionName="interiorFireExtinguisherCondition"
            commentName="interiorFireExtinguisherComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Torch"
            conditionName="interiorTorchCondition"
            commentName="interiorTorchComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Warning triangle"
            conditionName="interiorWarningTriangleCondition"
            commentName="interiorWarningTriangleComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Vehicle log book"
            conditionName="interiorLogBookCondition"
            commentName="interiorLogBookComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Spare tyre & jack"
            conditionName="interiorSpareTyreCondition"
            commentName="interiorSpareTyreComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
        </div>

        {/* Fluids */}
        <div className="bg-white rounded-lg shadow-sm p-5 border-2 border-gray-300">
          <h3 className="font-bold text-lg text-gray-900 mb-4 uppercase">
            Fluids
          </h3>
          <ExternalConditionItem
            label="Engine oil level"
            conditionName="fluidsEngineOilCondition"
            commentName="fluidsEngineOilComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Coolant level"
            conditionName="fluidsCoolantCondition"
            commentName="fluidsCoolantComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Windscreen wash level"
            conditionName="fluidsWindscreenWashCondition"
            commentName="fluidsWindscreenWashComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Brake/clutch fluid"
            conditionName="fluidsBrakeClutchCondition"
            commentName="fluidsBrakeClutchComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Power steering fluid"
            conditionName="fluidsPowerSteeringCondition"
            commentName="fluidsPowerSteeringComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Condition of battery"
            conditionName="fluidsBatteryCondition"
            commentName="fluidsBatteryComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Oil or waste leaks"
            conditionName="fluidsLeaksCondition"
            commentName="fluidsLeaksComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
        </div>

        {/* Function Checks Before Starting Journey */}
        <div className="bg-white rounded-lg shadow-sm p-5 border-2 border-gray-300">
          <h3 className="font-bold text-lg text-gray-900 mb-4 uppercase">
            Function Checks Before Starting the Journey
          </h3>
          <ExternalConditionItem
            label="Warning light"
            conditionName="functionWarningLightCondition"
            commentName="functionWarningLightComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="All lights"
            conditionName="functionAllLightsCondition"
            commentName="functionAllLightsComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Horn"
            conditionName="functionHornCondition"
            commentName="functionHornComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Washers & wipers"
            conditionName="functionWashersWipersCondition"
            commentName="functionWashersWipersComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Brake"
            conditionName="functionBrakeCondition"
            commentName="functionBrakeComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
          <ExternalConditionItem
            label="Fuel"
            conditionName="functionFuelCondition"
            commentName="functionFuelComment"
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
        </div>

        {/* Driver Declaration and Signature */}
        <div className="bg-white rounded-lg shadow-sm p-5 border-2 border-gray-300">
          <p className="text-sm text-gray-700 mb-4 italic">
            All the items above have been checked and any defects and omissions
            reported.
          </p>
          <div className="flex items-start">
            <input
              type="checkbox"
              id="driverSignature"
              name="driverSignature"
              checked={formData.driverSignature}
              onChange={handleInputChange}
              className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              required
            />
            <label
              htmlFor="driverSignature"
              className="ml-3 text-sm font-medium text-gray-700 cursor-pointer"
            >
              I agree that all reported information is correct
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 -mx-4">
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                Submitting...
              </div>
            ) : (
              todayCheck?.status === "rejected"
                ? "Resubmit Daily Check"
                : "Submit Daily Check"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DailyVehicleCheck;
